import { useMutation, useQueryClient } from '@tanstack/react-query';

import { QueryKeys } from '../keys';
import { request } from '../request';
import { userQueryOptions } from './useUser';

export const useFavoriteBrand = (id: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (isFavorite: boolean) => {
      await request(`/brands/${id}/favorite`, {
        method: isFavorite ? 'POST' : 'DELETE',
      });
    },
    // Optimistically update
    onMutate: async (isFavorite) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(userQueryOptions);

      // Snapshot the previous value
      const previousUser = queryClient.getQueryData(userQueryOptions.queryKey);

      // Optimistically update to the new value
      queryClient.setQueryData(userQueryOptions.queryKey, (oldUser) =>
        oldUser
          ? {
              ...oldUser,
              brands: oldUser.brands.map((brand) => (brand.id === id ? { ...brand, isFavorite } : { ...brand, isFavorite: false })),
            }
          : oldUser,
      );

      // Return a context object with the snapshotted value
      return { previousUser };
    },
    // If the mutation fails, use the context we returned above
    onError: (error, variables, context) => {
      queryClient.setQueryData(userQueryOptions.queryKey, context?.previousUser);
    },
    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.user });
    },
  });
};
